// Выбор микрофона: явный выбор пользователя (localStorage, всегда побеждает)
// + однократное авто-применение микрофона прошлого эфира при заходе на сайт.
// Ручной выбор переживает F5 и никогда не перебивается автоматикой.

const SELECTED_KEY = 'besedka.micSelected';
const LAST_LIVE_KEY = 'besedka.micLastLive';

export interface MicChoice {
  deviceId: string;
  label: string;
}

export type StoredSelection = 'default' | MicChoice;

export type AutoApplyResult =
  | { kind: 'applied'; device: MicChoice }
  | { kind: 'missing'; label: string }
  | null;

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* приватный режим и т.п. — просто без памяти */
  }
}

let autoApplied = false;
/** Микрофон прошлого эфира, применённый автоматикой в этой загрузке страницы. */
let autoDevice: MicChoice | null = null;
const listeners = new Set<() => void>();

export function getSelected(): StoredSelection | null {
  return readJson<StoredSelection>(SELECTED_KEY);
}

/** Явный выбор из дропдауна. Единственное место, где пишется micSelected. */
export function select(choice: StoredSelection): void {
  writeJson(SELECTED_KEY, choice);
  for (const cb of listeners) cb();
}

/** Запоминаем устройство, которым реально писали live-эфир. */
export function rememberLive(deviceId: string, label: string): void {
  if (!deviceId && !label) return;
  writeJson(LAST_LIVE_KEY, { deviceId, label, at: Date.now() } satisfies MicChoice & { at: number });
}

export async function listInputs(): Promise<MediaDeviceInfo[]> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((d) => d.kind === 'audioinput');
}

/**
 * Однократно (на загрузку страницы) применяет микрофон прошлого эфира, если
 * нет явного выбора. Звать ДО первого захвата (LiveKit/recorder). Возвращает,
 * что показать пользователю: применили / прошлый пропал / ничего.
 */
export async function resolveAutoOnce(): Promise<AutoApplyResult> {
  if (autoApplied) return null;
  autoApplied = true;
  if (getSelected() !== null) return null; // явный выбор всегда побеждает
  const last = readJson<MicChoice>(LAST_LIVE_KEY);
  if (!last?.deviceId && !last?.label) return null;
  try {
    const inputs = await listInputs();
    // deviceId стабилен для origin после выданного пермишена, но перестрахуемся label'ом
    const match =
      inputs.find((d) => d.deviceId === last.deviceId) ||
      (last.label ? inputs.find((d) => d.label && d.label === last.label) : undefined);
    if (match && match.deviceId !== 'default') {
      autoDevice = { deviceId: match.deviceId, label: match.label || last.label };
      return { kind: 'applied', device: autoDevice };
    }
    if (!match) return { kind: 'missing', label: last.label || 'прошлый микрофон' };
  } catch {
    /* enumerate недоступен — живём с системным */
  }
  return null;
}

/** null = без ограничения (системный по умолчанию). */
export function getPreferredDeviceId(): string | null {
  const selected = getSelected();
  if (selected !== null) return selected === 'default' ? null : selected.deviceId;
  return autoDevice?.deviceId ?? null;
}

/** Активный выбор для подсветки в дропдауне: 'default' | deviceId. */
export function getActiveChoice(): string {
  return getPreferredDeviceId() ?? 'default';
}

export function getAudioConstraint(base: MediaTrackConstraints): MediaTrackConstraints {
  const deviceId = getPreferredDeviceId();
  return deviceId ? { ...base, deviceId: { exact: deviceId } } : base;
}

/** Подписка на смену выбора и на devicechange ОС. */
export function onChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

navigator.mediaDevices?.addEventListener?.('devicechange', () => {
  for (const cb of listeners) cb();
});
